import React, { useEffect, useRef, useState } from "react";
import FundesciWhiteLogo from "../../assets/img/common/fundesci-white-logo.svg";
import FundesciBlackLogo from "../../assets/img/common/fundesci-black-logo.svg";
import { Link, NavLink, redirect, useLocation } from "react-router-dom";
import { currencyIcons } from "../../utils/currencies";

import { useChainId, useChains, useAccount, useBalance } from "wagmi";
import { useConnectModal, useChainModal } from "@rainbow-me/rainbowkit";
import "../../styles/Navbar.css";
import useOutsideClick from "../../hooks/useOutsideClick";
import ConnectWalletModal from "../navbar/ConnectWalletModal";
import { FaUserCircle, FaWallet } from "react-icons/fa";
import { RxHamburgerMenu } from "react-icons/rx";
import Container from "./Container";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGeneral,
  setHideNavbar,
  setShowAccountPopup,
  setShowRegisterModal,
  setShowAuthModal,
} from "../../redux/slices/generalSlice";

import HamburgerMenu from "./HamburgerMenu";
import useWindowSize from "../../hooks/useWindowSize";
import ExternalLink from "./ExternalLink";
import { publicAddress } from "../../utils/localData";
import NewNotifIcon from "./NewNotifIcon";
import WalletDisplay from "./WalletDisplay";

const Navbar = ({ isDark }) => {
  // Array of navigation links
  const links = [
    {
      title: "Home",
      path: "/",
    },
    {
      title: "Projects",
      path: "/projects",
    },
    {
      title: "Discovery",
      path: "/discovery",
    },
    {
      title: "Communities",
      path: "/communities",
    },
    {
      title: "About",
      path: "/about",
    },
    {
      title: "Contact",
      path: "/contact",
    },
  ];

  // Redux state and dispatch setup
  const { showRegisterModal, isLoggedIn, isHideNavbar } =
    useSelector(selectGeneral);
  const dispatch = useDispatch();

  // Current location pathname
  const { pathname } = useLocation();

  // State variables for UI interactions
  const [showProfilePopUp, setShowProfilePopUp] = useState(false);
  const [navbarClass, setNavbarClass] = useState("");
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);

  // Refs for UI elements
  const profilePopUpRef = useRef(null);
  const profileBtnRef = useRef(null);
  const registerModalRef = useRef(null);

  // State to track scroll position
  const [scrollData, setScrollData] = useState({
    y: 0,
    lastY: 0,
  });

  // Get window size hook
  const { windowSize } = useWindowSize();
  
  // Get current chain using Wagmi v2 hooks
  const chainId = useChainId();
  const chains = useChains();
  const chain = chains?.find((c) => c.id === chainId);
  
  // Get wallet connection status
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { openChainModal } = useChainModal();

  // Handle scroll event to show/hide navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrollData((lastState) => ({
        y: window.scrollY,
        lastY: lastState.y,
      }));
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Hide navbar when scrolling down
  useEffect(() => {
    if (scrollData.y > 50 && scrollData.y - scrollData.lastY > 0) {
      dispatch(setHideNavbar(true));
    } else {
      dispatch(setHideNavbar(false));
    }
  }, [scrollData]);

  // Change navbar appearance on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50 && !showWalletModal) {
        setNavbarClass(
          isDark
            ? "bg-[#67676770] max-mlg:bg-[#dbbbee70]"
            : "bg-[#c6bebe6e] max-mlg:bg-[#dbbbee70]"
        );
        setNavbarScrolled(true);
      } else {
        setNavbarScrolled(false);
        // On pages with dark hero sections (profile, community, edit profile), add bg for text visibility
        if (
          pathname.includes("/profile") || 
          pathname.includes("/communities/") ||
          pathname.includes("/edit-profile")
        ) {
          setNavbarClass("bg-white/90 backdrop-blur-md shadow-sm");
        } else {
          setNavbarClass("");
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [showWalletModal, pathname, isDark]);

  // Handle profile popup visibility
  const handleProfileClick = () => {
    setShowProfilePopUp(true);
  };

  // Hide profile popup on outside click
  const hideProfile = () => {
    setShowProfilePopUp(false);
  };
  useOutsideClick(
    profilePopUpRef,
    hideProfile,
    profileBtnRef,
    showProfilePopUp
  );

  // Toggle the display of the registration modal
  const handleShowRegisterModal = () => {
    dispatch(setShowRegisterModal(!showRegisterModal));
  };
  useOutsideClick(
    registerModalRef,
    handleShowRegisterModal,
    null,
    showRegisterModal
  );

  // Note: Web3Modal replaced with RainbowKit - use openConnectModal from useConnectModal

  // Account address from wagmi
  const { address } = useAccount();

  // Fetch account balance data
  const { data: balanceData } = useBalance({
    address: publicAddress() || address,
    enabled: isConnected && (address || publicAddress()),
  });

  // RainbowKit handles modal closing automatically when connection is established

  return (
    <div
      className={`w-full poppins-font flex justify-center items-end top-0 !fixed z-50 transition-all duration-500 ease-in-out ${
        isHideNavbar ||
        (scrollData?.y > 0 && pathname?.startsWith("/discovery"))
          ? "-translate-y-[100%]"
          : ""
      } 
     
      `}
    >
      <nav
        className={`w-full flex justify-between items-end pb-4 z-50 transition-all duration-200 px-4 sm:px-6 lg:px-8 ${navbarClass} ${
          navbarScrolled && !pathname?.startsWith("/discovery")
            ? "mlg:mt-0 backdrop-blur-md"
            : (pathname.includes("/profile") || pathname.includes("/communities/") || pathname.includes("/edit-profile")) 
              ? "" 
              : ""
        } `}
      >
        <section className="flex gap-x-5 ">
          <Link to={"/"}>
            <img
              src={FundesciWhiteLogo}
              alt="FundesciLogo"
              className={`-mb-1 pt-1.5 lg:mt-5 h-12 mlg:h-14 lg:h-16 ${
                isDark &&
                ((windowSize.width < 850 && !navbarScrolled) ||
                  windowSize.width > 850)
                  ? "flex"
                  : "hidden"
              }`}
            />
            <img
              src={FundesciBlackLogo}
              alt="FundesciLogo"
              className={`-mb-1 pt-1.5 lg:mt-5 h-12 mlg:h-14 lg:h-16 ${
                !isDark || (windowSize.width < 850 && navbarScrolled)
                  ? "flex"
                  : "hidden"
              }`}
            />
          </Link>
          <div className="gap-x-2 xl:gap-x-4 hidden mlg:flex ">
            {links.map((item, index) => (
              <NavLink
                key={item.path + index}
                to={item.path}
                className={({ isActive }) =>
                  `${
                    isDark ? "text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" : !isActive && "text-black"
                  } pt-7 lg:pt-10 px-2 lg:px-3 xl:px-4 pb-2 ${
                    isActive &&
                    "border-b-4 border-[#792cf3]  bg-main-gradient text-white "
                  } text-xs lg:text-sm whitespace-nowrap transition-all duration-200 hover:text-primary `
                }
              >
                {item.title}
              </NavLink>
            ))}{" "}
            <ExternalLink
              key="documentation"
              href={
                "https://fundesci.gitbook.io/docs/introduction/welcome-to-fundesci"
              }
              className={`${
                isDark ? "!text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" : "!text-black"
              } pt-7 lg:pt-10 px-2 lg:px-3 xl:px-4 pb-2 text-xs lg:text-sm whitespace-nowrap transition-all duration-200 hover:!text-primary `}
            >
              Docs
            </ExternalLink>
          </div>
        </section>
        <section className="flex items-center lg:pb-2 ">
          {/* <div className="hidden mlg:flex">
            {isDark ? (
              <div className="flex">
                <span className="relative flex items-end">
                  {isLoggedIn && (
                    <button
                      className="relative"
                      ref={profileBtnRef}
                      onClick={handleProfileClick}
                    >
                      <FaUserCircle
                        className="cursor-pointer text-xl md:text-2xl lg:text-3xl mr-5 h-7 "
                        color="white"
                      />
                      <NewNotifIcon className="absolute -left-[2px] lg:-left-0.5 bottom-[1px] lg:-bottom-0.5 w-3 lg:w-3.5 h-3 lg:h-3.5" />
                    </button>
                  )}
                  {showProfilePopUp && (
                    <ProfilePopUp
                      profilePopUpRef={profilePopUpRef}
                      showProfilePopUp={showProfilePopUp}
                      hideProfile={hideProfile}
                      isDark={isDark}
                      setShowProfilePopUp={setShowProfilePopUp}
                    />
                  )}
                </span>
              </div>
            ) : (
              <>
                <span className="relative flex items-end">
                  {isLoggedIn && (
                    <button
                      className="relative"
                      ref={profileBtnRef}
                      onClick={handleProfileClick}
                    >
                      <FaUserCircle
                        color="#3c2166"
                        className="cursor-pointer text-xl md:text-2xl lg:text-3xl mr-5 h-7 "
                      />
                      <NewNotifIcon className="absolute -left-[2px] lg:-left-0.5 bottom-[1px] lg:-bottom-0.5 w-3 lg:w-3.5 h-3 lg:h-3.5" />
                    </button>
                  )}
                  {showProfilePopUp && (
                    <ProfilePopUp
                      profilePopUpRef={profilePopUpRef}
                      showProfilePopUp={showProfilePopUp}
                      hideProfile={hideProfile}
                      isDark={isDark}
                      setShowProfilePopUp={setShowProfilePopUp}
                      setShowRegisterModal={setShowRegisterModal}
                      showRegisterModal={showRegisterModal}
                    />
                  )}
                </span>
              </>
            )}
          </div> */}

          {/* <div className="hidden xlg:flex items-end pb-1">
            <ConnectButton
              chainStatus={"full"}
              showBalance={false}
              accountStatus="full"
            />
          </div>

          <div className="flex xlg:hidden items-end pb-1">
            <ConnectButton
              chainStatus={"icon"}
              showBalance={false}
              accountStatus="avatar"
            />
          </div> */}

          {/* ----------   custom RainbowKit connect wallet button ---------- */}
          {isConnected && isLoggedIn ? (
            /* Wallet connected AND logged in - show full account UI */
            <>
            <button
              onClick={() => openChainModal?.()}
              className="text-secondary text-[10px] sm:text-xs flex gap-1 items-center rounded-xl ml-1 px-1.5 py-2 h-8 sm:h-10 bg-custom-gray-200 hover:bg-custom-gray-400 transition-all duration-200 ease-in-out border border-custom-gray-500"
            >
              <img
                src={
                  currencyIcons?.[
                    chain?.name === "Optimism" 
                      ? "OP" 
                      : chain?.name === "Arbitrum One" 
                      ? "ARB" 
                      : chain?.nativeCurrency?.symbol
                  ]
                }
                className="h-5 min-w-5 w-5"  // Set fixed height and width for the image
                alt={chain?.name}
              />
            </button>
                      
            <button
              onClick={() => dispatch(setShowAccountPopup(true))}
              className="text-secondary text-[10px] sm:text-xs flex gap-1 sm:gap-2 items-center rounded-xl ml-1 px-1.5 sm:px-2 py-2 h-8 sm:h-10 bg-custom-gray-200 hover:bg-custom-gray-400 transition-all duration-200 ease-in-out border border-custom-gray-500"
            >
              <div className="relative">
                <NewNotifIcon className="absolute left-0 top-0 w-2 h-2 md:w-2.5 md:h-2.5" />
                <FaUserCircle
                  color="#3c2166"
                  className="cursor-pointer text-base sm:text-lg"
                />
              </div>
              <span className="hidden md:flex text-[10px]">
                {Number(balanceData?.formatted).toFixed(3)}{" "}
                {balanceData?.symbol}
              </span>
              <WalletDisplay
                address={address}
                fallback={address ? undefined : "•••"}
                className="font-bold text-[10px] sm:text-xs"
                showEnsBadge={false}
              />
            </button>
          </>
          ) : isConnected && !isLoggedIn ? (
            /* Wallet connected but NOT logged in - show wallet info + Sign Up button */
            <>
              <button
                onClick={() => openChainModal?.()}
                className="text-secondary text-[10px] sm:text-xs flex gap-1 items-center rounded-xl ml-1 px-1.5 py-2 h-8 sm:h-10 bg-custom-gray-200 hover:bg-custom-gray-400 transition-all duration-200 ease-in-out border border-custom-gray-500"
              >
                <img
                  src={
                    currencyIcons?.[
                      chain?.name === "Optimism" 
                        ? "OP" 
                        : chain?.name === "Arbitrum One" 
                        ? "ARB" 
                        : chain?.nativeCurrency?.symbol
                    ]
                  }
                  className="h-5 min-w-5 w-5"
                  alt={chain?.name}
                />
              </button>
              <button
                onClick={() => dispatch(setShowAccountPopup(true))}
                className="text-secondary text-[10px] sm:text-xs flex items-center ml-1 px-1.5 sm:px-2 py-2 h-8 sm:h-10 bg-custom-gray-200 hover:bg-custom-gray-400 border border-custom-gray-500 rounded-xl transition-all duration-200 ease-in-out cursor-pointer"
                title="Click to manage wallet"
              >
                <FaWallet className="text-xs mr-1" />
                <WalletDisplay
                  address={address}
                  fallback={address ? undefined : "•••"}
                  className="font-bold text-[10px] sm:text-xs"
                  showEnsBadge={false}
                />
              </button>
              <button
                onClick={() => dispatch(setShowAuthModal({ show: true }))}
                className="text-white text-xs lg:text-sm rounded-3xl px-2 sm:px-3 py-2 h-8 sm:h-10 bg-[#792CF3] hover:bg-[#6b25d8] transition-all duration-200 ease-in-out whitespace-nowrap ml-2"
                title="Sign up to create projects and communities"
              >
                <span className="hidden sm:inline">Sign Up / Log In</span>
                <span className="sm:hidden">Sign Up</span>
              </button>
            </>
          ) : isLoggedIn ? (
            /* Social auth user - show profile button (wallet connect moved to AccountPopup) */
            <button
              onClick={() => dispatch(setShowAccountPopup(true))}
              className="text-secondary text-[10px] sm:text-xs flex gap-1 sm:gap-2 items-center rounded-xl ml-1 px-1.5 sm:px-2 py-2 h-8 sm:h-10 bg-custom-gray-200 hover:bg-custom-gray-400 transition-all duration-200 ease-in-out border border-custom-gray-500"
            >
              <div className="relative">
                <NewNotifIcon className="absolute left-0 top-0 w-2 h-2 md:w-2.5 md:h-2.5" />
                <FaUserCircle
                  color="#3c2166"
                  className="cursor-pointer text-base sm:text-lg"
                />
              </div>
              <span className="hidden md:flex font-medium text-xs">My Account</span>
            </button>
          ) : (
            /* Not logged in - show both Connect Wallet and Sign Up buttons */
            <>
              <button
                onClick={() => openConnectModal?.()}
                className="text-primary text-xs lg:text-sm rounded-3xl px-2 sm:px-3 py-2 h-8 sm:h-10 bg-primary/10 hover:bg-primary/20 transition-all duration-200 ease-in-out whitespace-nowrap border border-primary/30 flex items-center gap-1.5"
                title="Connect wallet to fund projects and join communities"
              >
                <FaWallet className="text-xs" />
                <span className="hidden sm:inline">Connect Wallet</span>
                <span className="sm:hidden">Wallet</span>
              </button>
              <button
                onClick={() => dispatch(setShowAuthModal({ show: true }))}
                className="text-white text-xs lg:text-sm rounded-3xl px-2 sm:px-3 py-2 h-8 sm:h-10 bg-[#792CF3] hover:bg-[#6b25d8] transition-all duration-200 ease-in-out whitespace-nowrap ml-2"
                title="Sign up to create projects and communities"
              >
                <span className="hidden sm:inline">Sign Up / Log In</span>
                <span className="sm:hidden">Sign Up</span>
              </button>
            </>
          )}

          <button
            onClick={() => {
              setShowHamburgerMenu(true);
            }}
            className="mlg:hidden ml-3 -mt-1"
          >
            <RxHamburgerMenu
              color={!isDark || navbarScrolled ? "#373737" : "#ffffff"}
              size={30}
            />
          </button>
        </section>
        {showWalletModal && (
          <ConnectWalletModal
            setShow={setShowWalletModal}
            show={showWalletModal}
          />
        )}
        <HamburgerMenu
          setShow={setShowHamburgerMenu}
          show={showHamburgerMenu}
        />
      </nav>{" "}
    </div>
  );
};

export default Navbar;
